import {Call, SfuModels} from '@stream-io/video-client';
import {AudioSource} from '../model/AudioSource';
import {logUtishaDebug, logUtishaError, logUtishaTrace, logUtishaWarn} from 'src/app/util/log';
import {MusicPlayer} from './helpers/MusicPlayer';
import {acquireMicrophoneWithFallback} from './helpers/acquireMedia';
import {MusicPlayback} from '../model/SessionState';
import {SessionCommands} from '../model/SessionCommands';
import {SessionStore} from '../model/SessionStore';
import {AsyncSessionResult, SessionResult} from '../model/AsyncSessionResult';
import {debounce, filter, firstValueFrom, interval, Subject, Subscription} from 'rxjs';

/**
 * Interface for tracking audio connections
 */
interface AudioConnection {
  context: AudioContext;
  source: MediaElementAudioSourceNode;
  element?: HTMLAudioElement;
}

/**
 * Class responsible for managing audio sources and mixing them with the microphone stream
 * Handles the registration, playback, and removal of audio sources for streaming
 */
export class AudioPublisher {
  private audioSources: AudioSource[] = [];
  private audioConnections: Map<string, AudioConnection> = new Map();
  private hiddenAudioContainer: HTMLElement;
  private musicPlayer?: MusicPlayer;
  private mix$ = new Subject<void>();
  private mixSubscription?: Subscription;
  private musicListenerSubscriptions: Subscription[] = [];
  private audioContext?: AudioContext;

  /**
   * Creates a new AudioPublisher instance
   * @param call The Stream Video Call instance to use for publishing audio
   * @param commands The session commands for music playback control
   * @param sessionStore The session store for reading/writing state
   */
  constructor(private call: Call, private commands?: SessionCommands, private sessionStore?: SessionStore) {
    // Initialize AudioContext
    this.getAudioContext();

    // Create container for hidden audio elements if it doesn't exist
    this.registerMusicListeners();
    if (!document.querySelector('#hidden-audio-container')) {
      this.hiddenAudioContainer = document.createElement('div');
      this.hiddenAudioContainer.setAttribute('id', 'hidden-audio-container');
      this.hiddenAudioContainer.style.display = 'none';
      document.body.appendChild(this.hiddenAudioContainer);
    } else {
      this.hiddenAudioContainer = document.querySelector('#hidden-audio-container') as HTMLElement;
    }

    this.mixSubscription = this.mix$.pipe(
      debounce(() => interval(300)),
    ).subscribe(() => this.mixAndPublishAudioSources());
  }

  /**
   * Gets all registered audio sources
   */
  public getAudioSources(): AudioSource[] {
    return [...this.audioSources];
  }

  /**
   * Gets playing audio sources
   */
  public getPlayingAudioSources(): AudioSource[] {
    return this.audioSources.filter(source => source.playing);
  }

  /**
   * Adds an audio source that can be mixed with the stream audio
   * @param fileUrl URL to the audio file (e.g., '/assets/audio/beep1.wav')
   * @param name Display name for the audio source
   * @returns The created or existing AudioSource
   */
  public addAudio(fileUrl: string, name: string): AudioSource {
    // Check if audio source with this id already exists
    let audioSource = this.audioSources.find(source => source.src === fileUrl);

    if (!audioSource) {
      // Create new audio source
      audioSource = new AudioSource(fileUrl, name);

      // Add audio element to DOM for better browser support
      if (this.hiddenAudioContainer) {
        this.hiddenAudioContainer.appendChild(audioSource.audioElement);
      }

      // Add to sources list
      this.audioSources.push(audioSource);

      // Mix and publish all audio sources with microphone
      // The mixAndPublishAudioSources method will handle connecting the new source
      this.mix$.next();

      logUtishaDebug(`Added audio source: ${name} (${fileUrl}). Total sources: ${this.audioSources.length}`);
    }

    return audioSource;
  }

  /**
   * Removes an audio source by its id or name
   * @param identifier The id or name of the audio source to remove
   * @returns True if the audio source was found and removed, false otherwise
   */
  public removeAudio(identifier: string): boolean {
    // Find the index of the audio source with the given id or name
    const index = this.audioSources.findIndex(
      source => source.src === identifier || source.name === identifier
    );

    if (index === -1) {
      logUtishaDebug(`Audio source with identifier '${identifier}' not found`);
      return false;
    }

    // Get the audio source to remove
    const audioSource = this.audioSources[index];

    try {
      // Clean up the audio source
      audioSource.cleanup();

      // Remove from DOM if it was added
      if (this.hiddenAudioContainer && audioSource.audioElement.parentNode === this.hiddenAudioContainer) {
        this.hiddenAudioContainer.removeChild(audioSource.audioElement);
      }

      // Remove from sources array first to prevent it from being reconnected
      // during audio mixing
      this.audioSources.splice(index, 1);

      // Trigger the audio mixing update
      // The mixAndPublishAudioSources method will handle disconnecting the source
      this.mix$.next();

      logUtishaDebug(`Removed audio source: ${audioSource.name} (${audioSource.src}). Remaining sources: ${this.audioSources.length}`);
      return true;
    } catch (error) {
      logUtishaError(`Error removing audio source ${audioSource.name}: ${error}`);
      return false;
    }
  }

  /**
   * Updates the gain for an audio source
   * @param identifier The id or name of the audio source
   * @param gain New gain value (0.0 to 1.0)
   * @returns True if the audio source was found and updated, false otherwise
   */
  public updateAudioGain(identifier: string, gain: number): boolean {
    const audioSource = this.audioSources.find(
      source => source.src === identifier || source.name === identifier
    );

    if (!audioSource) {
      logUtishaDebug(`Audio source with identifier '${identifier}' not found for gain update`);
      return false;
    }

    // Update the gain on the AudioSource object
    // This will also update the gain node if it exists
    audioSource.gain = gain;
    logUtishaDebug(`Updated gain for ${audioSource.name} to ${gain}`);

    return true;
  }

  /**
   * Disconnects all audio sources from their audio contexts
   * This helps prevent the "already connected" error when remixing
   */
  private disconnectAllAudioSources(): void {
    logUtishaDebug(`Disconnecting all audio sources. Active mixed sources: ${this.activeMixedSources.size}`);

    // First disconnect sources that are actively mixed
    if (this.activeMixedSources.size > 0) {
      for (const sourceId of this.activeMixedSources) {
        // Find the source in our audio sources array
        const source = this.audioSources.find(s => s.src === sourceId);
        if (source) {
          try {
            source.disconnectAudioNodes();
            logUtishaDebug(`Disconnected active mixed source ${source.name} from audio context`);
          } catch (error) {
            logUtishaError(`Error disconnecting active mixed source ${source.name}: ${error}`);
          }
        }
      }

      // Clear the active mixed sources set
      this.activeMixedSources.clear();
    }

    // Now disconnect any other audio sources that might not be in the active mixed set
    for (const source of this.audioSources) {
      try {
        // Use the AudioSource disconnectAudioNodes method
        source.disconnectAudioNodes();
        logUtishaDebug(`Disconnected audio source ${source.name} from audio context`);
      } catch (error) {
        logUtishaError(`Error disconnecting audio source ${source.name}: ${error}`);
      }
    }

    // Iterate through all connections and clean up any remaining elements and contexts
    for (const [id, connection] of this.audioConnections.entries()) {
      try {
        // Stop playback of the element if it exists
        if (connection.element) {
          connection.element.pause();
          // Remove any event listeners to prevent memory leaks
          connection.element.oncanplaythrough = null;
          connection.element.onerror = null;
          // Remove all event listeners
          connection.element.removeEventListener('ended', () => {
          });
        }

        // We don't close the audio context here as we want to reuse it
        // The context will be closed in the dispose method if needed
      } catch (error) {
        logUtishaError(`Error cleaning up audio connection ${id}: ${error}`);
      }
    }

    // Clear the connections map
    this.audioConnections.clear();
  }

  /**
   * Clears all audio sources
   */
  public clearAllAudioSources(): void {
    // Create a copy of the sources array to iterate over
    const sourcesToRemove = [...this.audioSources];

    // Remove each source individually
    for (const source of sourcesToRemove) {
      this.removeAudio(source.src);
    }
  }

  // Audio mixing variables for maintaining a consistent audio context
  private mixedAudioContext?: AudioContext;
  private mixedDestination?: MediaStreamAudioDestinationNode;
  private micSource?: MediaStreamAudioSourceNode;
  private micGain?: GainNode;
  private publishedStream?: MediaStream;

  // Track which audio sources are actively mixed in the current audio context
  private activeMixedSources: Set<string> = new Set<string>();

  /**
   * Handles the mixing of audio sources with the microphone stream without republishing
   * the entire audio stream unless necessary
   */
  private mixAndPublishAudioSources(): void {
    logUtishaDebug(`Mixing and publishing audio sources`);

    // Check if there are any audio sources
    if (this.audioSources.length > 0) {
      // If we already have a published mixed stream, we'll just update connections
      if (this.publishedStream && this.mixedAudioContext && this.mixedDestination) {
        // We can just add/remove sources to the existing context
        this.updateAudioSourceConnections().catch(err => {
          logUtishaError(`Error updating audio source connections: ${err}`);
          // If something goes wrong, fall back to full republishing
          this.republishEntireAudioStream();
        });
      } else {
        // No existing context or first time publishing - create everything
        this.republishEntireAudioStream();
      }
    } else {
      // No audio sources left, clean up and republish microphone only
      this.cleanupAudioMixing();
    }
  }

  /**
   * Updates the connections for audio sources without recreating the entire audio pipeline
   * This is used when adding or removing sources to avoid audio interruptions
   */
  private async updateAudioSourceConnections(): Promise<void> {
    if (!this.mixedAudioContext || !this.mixedDestination) {
      throw new Error('Audio context not initialized');
    }

    logUtishaDebug(`Updating audio source connections for ${this.audioSources.length} sources`);

    // Identify sources that need to be connected and disconnected
    const connectedSources = new Set(this.audioConnections.keys());
    const currentSources = new Set(this.audioSources.map(source => source.src));

    // Sources to disconnect (in connections but not in current sources)
    const sourcesToDisconnect = Array.from(connectedSources).filter(sourceId => !currentSources.has(sourceId));

    // Sources to connect (in current sources but not in connections)
    const sourcesToConnect = this.audioSources.filter(source => !connectedSources.has(source.src));

    // debugger;
    // Disconnect removed sources
    for (const sourceId of sourcesToDisconnect) {
      const connection = this.audioConnections.get(sourceId);
      if (connection) {
        try {
          // Find the corresponding audio source
          const source = this.audioSources.find(s => s.src === sourceId);
          if (source) {
            source.disconnectAudioNodes();
          } else {
            // If the source is no longer in our list, disconnect manually
            if (connection.source) {
              connection.source.disconnect();
            }
          }
          this.audioConnections.delete(sourceId);
          // Remove from active mixed sources
          this.activeMixedSources.delete(sourceId);
          logUtishaDebug(`Disconnected audio source ${sourceId} from audio mix`);
        } catch (error) {
          logUtishaError(`Error disconnecting audio source ${sourceId}: ${error}`);
        }
      }
    }

    // Connect new sources
    for (const source of sourcesToConnect) {
      try {
        logUtishaDebug(`Connecting new audio source ${source.name} to existing audio mix`);
        await source.connectToDestination(this.mixedAudioContext, this.mixedDestination);

        // Store the connection
        this.audioConnections.set(source.src, {
          context: this.mixedAudioContext,
          source: source.sourceNode!,
          element: source.clonedAudioElement
        });

        // Add to active mixed sources
        this.activeMixedSources.add(source.src);
      } catch (error) {
        logUtishaError(`Error connecting audio source ${source.name}: ${error}`);
      }
    }

    logUtishaDebug(`Audio source connections updated: ${sourcesToConnect.length} added, ${sourcesToDisconnect.length} removed`);
    logUtishaDebug(`Total active mixed sources: ${this.activeMixedSources.size}`);
  }

  /**
   * Creates a completely new audio mixing setup and publishes it
   * This is used for initial setup or when dynamic updating fails
   */
  private async republishEntireAudioStream(): Promise<void> {
    // Clean up any existing audio context
    this.cleanupAudioMixing();

    try {
      // Stop the current audio publishing
      await this.call.stopPublish(SfuModels.TrackType.AUDIO);
    } catch (err) {
      logUtishaError(`Failed to stop publishing audio before remixing: ${err}`);
      return;
    }

    try {
      // Get microphone stream once (not a permanent subscription)
      const mediaStream = await firstValueFrom(
        this.call.microphone.state.mediaStream$.pipe(
          filter((ms): ms is MediaStream => !!ms && ms.getAudioTracks().length > 0)
        )
      );

      if (this.audioSources.length > 0) {
        logUtishaDebug(`${this.audioSources.length} audio sources are active, remixing with microphone`);

        // Create a new AudioContext
        this.mixedAudioContext = this.getAudioContext();

        // Create destination for the mixed audio
        const audioContext = this.mixedAudioContext!;
        logUtishaDebug('Creating new mixed destination');
        this.mixedDestination = audioContext.createMediaStreamDestination();

        // Add microphone to the mix - keep microphone at full volume
        this.micSource = audioContext.createMediaStreamSource(mediaStream);
        this.micGain = audioContext.createGain();
        this.micGain.gain.value = 1.0; // Full volume for microphone
        this.micSource.connect(this.micGain);
        this.micGain.connect(this.mixedDestination);

        logUtishaDebug('Connected microphone to audio mix destination');

        // Connect all active audio sources
        for (const source of this.audioSources) {
          try {
            logUtishaDebug(`Connecting audio source ${source.name} to audio mix destination`);
            // This will reuse existing nodes if they exist
            await source.connectToDestination(audioContext, this.mixedDestination);

            // Store the connection in our map for reference
            this.audioConnections.set(source.src, {
              context: audioContext,
              source: source.sourceNode!,
              element: source.clonedAudioElement
            });
          } catch (connectionError) {
            logUtishaError(`Error connecting audio source ${source.name}: ${connectionError}`);
          }
        }

        // Get the mixed track and publish it
        const mixedTrack = this.mixedDestination.stream.getAudioTracks()[0];
        if (mixedTrack) {
          this.publishedStream = new MediaStream([mixedTrack]);
          try {
            await this.call.publish(this.publishedStream, SfuModels.TrackType.AUDIO);
            logUtishaDebug(`Successfully published mixed audio with ${this.audioSources.length} sources`);
          } catch (err) {
            logUtishaError(`Failed to publish mixed audio: ${err}`);
          }
        }
      } else {
        // No active audio sources, restore original microphone stream
        try {
          await this.call.publish(mediaStream, SfuModels.TrackType.AUDIO);
          logUtishaDebug('Restored original microphone stream after audio source removal');
        } catch (err) {
          logUtishaError(`Failed to restore original microphone stream: ${err}`);
          this.reinitializeMicrophone();
        }
      }
    } catch (error) {
      logUtishaError(`Error in republishEntireAudioStream: ${error}`);
    }
  }

  /**
   * Cleans up the audio mixing context and connections
   */
  private cleanupAudioMixing(): void {
    logUtishaDebug(`Cleaning up audio mixing resources. Active mixed sources: ${this.activeMixedSources.size}`);

    // Disconnect all audio sources from the context
    this.disconnectAllAudioSources();

    // Ensure the activeMixedSources set is cleared
    this.activeMixedSources.clear();

    // Disconnect and clean up microphone nodes
    if (this.micGain) {
      try {
        this.micGain.disconnect();
      } catch (e) {}
      this.micGain = undefined;
    }

    if (this.micSource) {
      try {
        this.micSource.disconnect();
      } catch (e) {}
      this.micSource = undefined;
    }

    // Clear reference to the published stream
    this.publishedStream = undefined;

    // We don't close the audioContext here to allow it to be reused
    // but we do clear the reference to the destination
    this.mixedDestination = undefined;

    if (this.audioSources.length === 0) {
      this.restoreMicrophoneOnlyStream();
    }
  }

  private async restoreMicrophoneOnlyStream(): Promise<void> {
    try {
      await this.call.stopPublish(SfuModels.TrackType.AUDIO);
      const mediaStream = await firstValueFrom(
        this.call.microphone.state.mediaStream$.pipe(
          filter((ms): ms is MediaStream => !!ms && ms.getAudioTracks().length > 0)
        )
      );
      await this.call.publish(mediaStream, SfuModels.TrackType.AUDIO);
      logUtishaDebug('Published microphone-only stream after cleaning up audio mixing');
    } catch (err) {
      logUtishaError(`Failed to restore microphone-only stream: ${err}`);
      this.reinitializeMicrophone();
    }
  }

  public playAudio(identifier: string): boolean {
    const audioSource = this.audioSources.find(
      source => source.src === identifier || source.name === identifier
    );

    if (!audioSource) {
      logUtishaDebug(`Audio source with identifier '${identifier}' not found for playback`);
      return false;
    }

    audioSource.play();
    return true;
  }

  /**
   * Reinitializes the microphone in case it's not available after audio source removal
   */
  private reinitializeMicrophone(): void {
    logUtishaDebug(`Attempting to reinitialize microphone`);
    acquireMicrophoneWithFallback().then(stream => {
      this.call.publish(stream, SfuModels.TrackType.AUDIO).then(() => {
        logUtishaDebug('Successfully reinitialized microphone after audio source removal');
      }).catch(err => {
        logUtishaWarn(`Failed to publish reinitialized microphone: ${err}`);
      });
    }).catch(err => {
      logUtishaError(`Failed to get microphone stream for reinitialization: ${err}`);
    });
  }

  /**
   * Start background music playback with a list of MP3 files
   * @param musicPlayback The music playback configuration
   */
  public async startBackgroundMusic(musicPlayback: MusicPlayback): Promise<AsyncSessionResult> {
    logUtishaDebug(`Starting background music playback`);
    try {
      // If no MP3 list is provided, nothing to play
      if (!musicPlayback.mp3List || musicPlayback.mp3List.length === 0) {
        logUtishaError('No MP3 list provided for music playback');
        return SessionResult.failure('No MP3 list provided');
      }

      // Create a new music player if we don't have one, or if we need to restart it
      if (!this.musicPlayer && this.sessionStore) {
        logUtishaDebug('Creating new MusicPlayer instance');
        // Use the volume from musicPlayback or from state or default to 0.5
        const volume = musicPlayback.volume !== undefined ? musicPlayback.volume : this.sessionStore.snapshot.media.musicVolume || 0.5;
        // Ensure the volume is set in the state before creating the player
        this.sessionStore.update({ media: { musicVolume: volume } });

        this.musicPlayer = new MusicPlayer(musicPlayback.mp3List, this.sessionStore, 'Background Music');

        // Add the music player's audio source to the audio publisher
        // Note: We're reusing the same AudioSource for all tracks, so we only need to add it once
        this.audioSources.push(this.musicPlayer.audioSource);

        // Mix and publish audio sources with microphone
        // This is crucial for remote users to hear the music

        this.mix$.next();

        const musicPlayer = this.musicPlayer;
        // Wait for the audio to be ready before playing
        const audioElement = musicPlayer.audioSource.audioElement;

        // Ensure audio is loaded before starting playback
        // const startPlayback = () => {
        //   setTimeout(() => {
        //     musicPlayer.play();
        //   }, 300);
        // };


        if (audioElement.readyState >= 2) { // HAVE_CURRENT_DATA or better
          await musicPlayer.play()
        } else {
          audioElement.addEventListener('canplay', () => musicPlayer.play(), { once: true });
        }
      }

      // Handle paused state
      if (musicPlayback.paused && this.musicPlayer) {
        if (this.musicPlayer.audioSource.playing) {
          logUtishaDebug('Pausing music playback');
          this.musicPlayer.pause();
        }
      } else if (this.musicPlayer) {
        if (!this.musicPlayer.audioSource.playing) {
          logUtishaDebug('Resuming music playback');
          this.musicPlayer.resume();
        }
      }

      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error starting background music: ', error);
      return SessionResult.failure(`Failed to start music: ${error.message}`);
    }
  }

  /**
   * Set the volume for background music
   * @param volume Volume level from 0.0 (silent) to 1.0 (full volume)
   */
  public setMusicVolume(volume: number): void {
    if (this.musicPlayer) {
      logUtishaDebug(`Background music volume set to ${volume}`);
      // Update the gain in the music player
      this.musicPlayer.setVolume(volume);
    }
  }

  /**
   * Stop background music playback
   */
  public async stopBackgroundMusic(): Promise<AsyncSessionResult> {
    logUtishaDebug(`Stopping background music playback`);
    try {
      if (this.musicPlayer) {
        // Stop the music
        this.musicPlayer.stop();

        // Remove the audio source from the audio publisher
        this.removeAudio(this.musicPlayer.audioSource.name);

        // Remove the reference to the music player
        this.musicPlayer = undefined;

        logUtishaDebug('Background music stopped and cleaned up');
      } else {
        logUtishaDebug('No active music player to stop');
      }

      return SessionResult.ok;
    } catch (error: any) {
      logUtishaError('Error stopping background music: ', error);
      return SessionResult.failure(`Failed to stop music: ${error.message}`);
    }
  }

  /**
   * Play the next track in the music playlist
   */
  public playNextMusicTrack(): void {
    if (this.musicPlayer) {
      logUtishaDebug('Playing next music track');
      // playNextTrack method handles stopping the current track internally
      this.musicPlayer.playNextTrack();
    } else {
      logUtishaDebug('Cannot play next track: no active music player');
      // If there's no active music player but music is requested and state is available
      if (this.sessionStore && this.sessionStore.snapshot.media.musicPlayback?.mp3List) {
        this.startBackgroundMusic({
          started: true,
          mp3List: this.sessionStore.snapshot.media.musicPlayback.mp3List
        }).catch(error => {
          logUtishaError('Error starting music from next track request:', error);
        });
      }
    }
  }

  /**
   * Register listeners for music control events from session state
   */
  /**
   * Gets the current AudioContext or creates a new one if needed
   * @returns The AudioContext instance
   */
  private getAudioContext(): AudioContext {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      logUtishaDebug('Created new AudioContext');
    } else if (this.audioContext.state === 'suspended') {
      // Resume if suspended (can happen after user interaction requirements)
      this.audioContext.resume().then(() => {
        logUtishaDebug('Resumed suspended AudioContext');
      }).catch(err => {
        logUtishaError(`Error resuming AudioContext: ${err}`);
      });
    }
    return this.audioContext;
  }

  /**
   * Properly disposes of resources when the publisher is no longer needed
   */
  public dispose(): void {
    // Unsubscribe from mix$ and music listener subscriptions
    this.mixSubscription?.unsubscribe();
    this.mixSubscription = undefined;
    for (const sub of this.musicListenerSubscriptions) {
      sub.unsubscribe();
    }
    this.musicListenerSubscriptions = [];

    // Clean up all audio mixing resources
    this.cleanupAudioMixing();

    // Disconnect all sources
    this.disconnectAllAudioSources();

    // Close the audio context if it exists
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        logUtishaDebug('AudioContext closed successfully');
      }).catch(err => {
        logUtishaError(`Error closing AudioContext: ${err}`);
      });
      this.audioContext = undefined;
    }

    // Also close the mixed audio context if it's different
    if (this.mixedAudioContext &&
        this.mixedAudioContext !== this.audioContext &&
        this.mixedAudioContext.state !== 'closed') {
      this.mixedAudioContext.close().then(() => {
        logUtishaDebug('Mixed AudioContext closed successfully');
      }).catch(err => {
        logUtishaError(`Error closing Mixed AudioContext: ${err}`);
      });
      this.mixedAudioContext = undefined;
    }

    // Clear all audio sources
    this.clearAllAudioSources();
  }

  private registerMusicListeners(): void {
    if (!this.commands || !this.sessionStore) {
      logUtishaWarn('Cannot register music listeners: no session commands/store provided');
      return;
    }

    // Register listener for background music control
    const musicStartedSub = this.commands.startMusic.requestTx
      .pipe(debounce(() => interval(300)))
      .subscribe(async (payload) => {
        if (payload?.started) {
          await this.startBackgroundMusic(payload);
        } else {
          await this.stopBackgroundMusic();
        }
      });
    if (musicStartedSub) this.musicListenerSubscriptions.push(musicStartedSub);

    // Register listener for next track command
    const musicNextSub = this.commands.skipMusic.requestTx
      .subscribe(() => {
        this.playNextMusicTrack();
      });
    if (musicNextSub) this.musicListenerSubscriptions.push(musicNextSub);

    // Listen for volume change requests
    const musicVolumeSub = this.sessionStore.musicVolume$
      .pipe(debounce(() => interval(500)))
      .subscribe((volume) => {
        if (volume !== undefined) {
          this.setMusicVolume(volume);
        }
      });
    if (musicVolumeSub) this.musicListenerSubscriptions.push(musicVolumeSub);

    logUtishaDebug('Music listeners registered');
  }
}
